'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import Link from 'next/link';
import Image from 'next/image';
import { ChevronDown, ArrowRight, Search, Zap, Loader, ArrowLeft, ArrowUp } from 'lucide-react';
import { type Electrician } from '@/app/lib/electricians';
import { slugify, cn } from '@/lib/utils';
import DaisyconAd from './DaisyconAd';
import { daisyconAds } from '@/app/lib/ads';
import electricianTips from '@/app/lib/electrician-tips.json';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function ElectricianList({ initialElectricians, allAreas, currentArea, provinceName }: { initialElectricians: Electrician[], allAreas?: string[], currentArea?: string, provinceName: string }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnly24h, setShowOnly24h] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [shuffledTips, setShuffledTips] = useState<string[]>([]);
  const [shuffledAds, setShuffledAds] = useState<string[]>([]);
  const [topAreas, setTopAreas] = useState<string[]>([]);
  const [otherAreas, setOtherAreas] = useState<string[]>([]);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  
  const provinceSlug = useMemo(() => slugify(provinceName), [provinceName]);

  useEffect(() => {
    setIsClient(true);
    setShuffledTips([...electricianTips].sort(() => 0.5 - Math.random()));
    setShuffledAds([...daisyconAds.electricianListAds].sort(() => 0.5 - Math.random()));

    if (allAreas) {
        const uniqueAreas = Array.from(new Set(allAreas.map(a => a.trim()).filter(Boolean)));
        setTopAreas(uniqueAreas.slice(0, 5));
        setOtherAreas(uniqueAreas.slice(5));
    }
  }, [allAreas]);

  useEffect(() => {
    if (!isClient) return;

    const handleScroll = () => {
      setShowScrollTopButton(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isClient]);

  const filteredElectricians = useMemo(() => {
    let list = [...initialElectricians];
    
    if (showOnly24h) {
        list = list.filter(e => e.is24h === true);
    }

    const normalizedSearch = searchTerm.toLowerCase().trim();
    
    if (normalizedSearch) {
        list = list.filter(e => 
            e.name.toLowerCase().includes(normalizedSearch) || 
            e.area.toLowerCase().includes(normalizedSearch) ||
            (e.extra2 || '').toLowerCase().includes(normalizedSearch) ||
            (e.extra || '').toLowerCase().includes(normalizedSearch)
        );
    } else if (currentArea && currentArea !== 'Alle') {
        list = list.filter(e => slugify(e.area) === slugify(currentArea));
    }
    
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [initialElectricians, currentArea, searchTerm, showOnly24h]);

  const itemsWithAds = useMemo(() => {
    if (!isClient) return filteredElectricians;

    const items: (Electrician | { type: string, id: string, html?: string, tip?: string })[] = [];
    let adCounter = 0;
    let tipCounter = 0;
    
    filteredElectricians.forEach((e, index) => {
        items.push(e);
        
        const position = index + 1;

        if (position % 4 === 0 && shuffledAds.length > 0) {
            const adHtml = shuffledAds[adCounter % shuffledAds.length];
            items.push({ type: 'ad', id: `ad-${position}`, html: adHtml });
            adCounter++;
        }
        
        if (position % 6 === 0 && shuffledTips.length > 0) {
            const tip = shuffledTips[tipCounter % shuffledTips.length];
            items.push({ type: 'tip', id: `tip-${position}`, tip });
            tipCounter++;
        }
    });
    
    return items;
  }, [filteredElectricians, isClient, shuffledTips, shuffledAds]);

  const renderPillButtons = () => {
    if (!allAreas || !provinceSlug) return null;

    const pills = [
      { area: 'Alle', link: `/elektriciens-in-${provinceSlug}` },
      ...topAreas.map(area => ({ area, link: `/elektriciens-in-${provinceSlug}/${slugify(area)}` })),
      ...(otherAreas.length > 0 ? [{ area: 'Meer...', isDropdown: true, link: '#' }] : [])
    ];

    const effectiveArea = currentArea || 'Alle';
    const isOtherAreaSelected = effectiveArea && otherAreas.includes(effectiveArea);

    const chunk = <T,>(arr: T[], size: number): T[][] =>
      Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
        arr.slice(i * size, i * size + size)
      );

    const otherAreasColumns = chunk(otherAreas, 10);

    return (
      <div className="relative w-full overflow-hidden">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 pr-12">
          {pills.map((pill) => {
            if (pill.isDropdown) {
              return (
                <DropdownMenu key="more-dropdown">
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={isOtherAreaSelected ? 'default' : 'secondary'}
                      className="rounded-full shrink-0 bg-white/90 text-foreground hover:bg-white"
                    >
                      {isOtherAreaSelected ? effectiveArea : 'Meer...'}
                      <ChevronDown className="ml-2 h-4 w-4 inline" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="p-0 max-w-[90vw] relative">
                    <div className="overflow-x-auto overflow-y-hidden no-scrollbar p-2">
                      <div className="flex gap-4">
                        {otherAreasColumns.map((column, colIndex) => (
                          <div key={colIndex} className="flex flex-col flex-shrink-0">
                            {column.map(area => (
                              <DropdownMenuItem key={area} asChild className="whitespace-nowrap">
                                <Link href={`/elektriciens-in-${provinceSlug}/${slugify(area)}`}>
                                  {area}
                                </Link>
                              </DropdownMenuItem>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }

            const pillSlug = pill.area === 'Alle' ? 'Alle' : slugify(pill.area);
            const isActive = (effectiveArea === 'Alle' && pill.area === 'Alle') || (effectiveArea && slugify(effectiveArea) === pillSlug);

            return (
              <Button
                key={pill.area}
                asChild
                variant={isActive ? 'default' : 'secondary'}
                className={`rounded-full shrink-0 ${!isActive ? 'bg-white/90 text-foreground hover:bg-white' : ''}`}
              >
                <Link href={pill.link}>
                  {pill.area}
                </Link>
              </Button>
            );
          })}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-slate-900/70 to-transparent" />
      </div>
    );
  };

  return (
    <div className="bg-muted min-h-screen">
      <div className="relative w-full h-[450px] bg-muted overflow-hidden">
        <Image
          src="/elektriciens-hero.webp"
          alt="Elektricien aan het werk"
          fill
          className="object-cover"
          data-ai-hint="electrician worker"
          priority
        />
        <div className="absolute inset-0 bg-slate-900/70 p-4 flex flex-col">
          <div className="flex justify-between items-start mb-4">
            <Button asChild variant="outline" size="sm" className="bg-white/10 text-white border-white/20 hover:bg-white/20 backdrop-blur-sm">
              <Link href="/elektriciens">
                <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar provincies
              </Link>
            </Button>
          </div>

          <div className="max-w-4xl mx-auto text-center text-white w-full flex flex-col justify-center flex-grow">
              <Zap className="h-10 w-10 text-primary mx-auto mb-4" />
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Elektriciens in {currentArea === 'Alle' || !currentArea ? `Provincie ${provinceName}` : currentArea}</h1>
              <p className="text-white/80 mb-8 max-w-2xl mx-auto">Vind een erkend elektricien voor installatie, keuring of spoedherstelling bij u in de buurt.</p>
              
              <div className="space-y-6">
                  <div className="relative max-w-md mx-auto">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input 
                          className="pl-10 h-12 bg-white/95 text-slate-900 text-base border-none focus-visible:ring-primary" 
                          placeholder="Zoek op naam of gemeente..." 
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                      />
                  </div>

                  <div className="flex items-center justify-center space-x-3">
                      <Switch 
                          id="24h-filter" 
                          checked={showOnly24h} 
                          onCheckedChange={setShowOnly24h}
                          className="data-[state=checked]:bg-primary"
                      />
                      <Label htmlFor="24h-filter" className="cursor-pointer font-medium text-white text-sm md:text-base">
                          Toon enkel 24/7 spoeddiensten
                      </Label>
                  </div>

                  <div className="mt-4">
                    {renderPillButtons()}
                  </div>
              </div>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto p-4 space-y-4 pb-20 mt-4">
        {(currentArea === 'Alle' || !currentArea) && !searchTerm && (
            <div className="p-4 bg-muted/30">
                <Card className="shadow-none border-0 bg-transparent">
                    <CardContent className="p-0">
                        <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/80 text-sm">
                            <h2 className="text-xl font-semibold text-foreground">Vind een Erkend Elektricien in Provincie {provinceName}</h2>
                            <p>
                                Welkom op kzoek.com. Het vinden van een betrouwbare elektricien is essentieel voor de veiligheid en het comfort van uw woning. Of het nu gaat om een kleine herstelling, een volledige renovatie of een officiële keuring, een vakman met de juiste expertise maakt het verschil.
                            </p>
                            <p>
                                Deze website helpt bewoners in België, met een focus op de provincie {provinceName}, om snel en eenvoudig een erkend elektricien bij u in de buurt te ontdekken. Gebruik de zoekfunctie of filter op gemeente om de juiste professional te vinden voor uw project. Bekijk profielen, check de 24/7 beschikbaarheid voor spoedgevallen en lees recensies van andere klanten.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )}

        <div className="flex justify-between items-center text-sm text-muted-foreground px-1">
            <span>{filteredElectricians.length} vakmannen gevonden</span>
        </div>

        {itemsWithAds.map((item) => {
            if ('type' in item) {
                if (item.type === 'ad' && item.html) return <DaisyconAd key={item.id} html={item.html} />;
                if (item.type === 'tip' && item.tip) return (
                    <Card key={item.id} className="bg-primary/5 border-primary/20 p-4">
                        <p className="text-sm italic text-muted-foreground">Tip: {item.tip}</p>
                    </Card>
                );
                return null;
            }

            const e = item as Electrician;
            return (
                <Card key={e.id} className="hover:border-primary transition-colors group">
                    <Link href={`/elektriciens-in-${provinceSlug}/${slugify(e.area)}/${e.slug}`} className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-card-foreground">
                        <div className="text-left flex-grow">
                            <div className="flex items-center justify-start gap-2 mb-1">
                                <CardTitle className="group-hover:text-primary transition-colors text-xl">{e.name}</CardTitle>
                                {e.is24h && <Badge className="bg-red-500 text-white font-bold animate-pulse-glow">24/7</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">{e.area} - {e.address}</p>
                            {e.extra2 && (
                                <div className="mt-3 bg-slate-100 border border-slate-200 rounded-lg p-3 flex items-start gap-2.5">
                                    <Zap className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
                                    <p className="text-sm text-slate-900 leading-snug font-semibold">
                                        {e.extra2}
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="shrink-0 w-full md:w-auto self-end md:self-center">
                            <div className={buttonVariants({ variant: 'default', className: "w-full md:w-auto group-hover:bg-primary/90 transition-colors" })}>
                                Bekijk profiel <ArrowRight className="ml-2 h-4 w-4" />
                            </div>
                        </div>
                    </Link>
                </Card>
            );
        })}

        {filteredElectricians.length === 0 && (
            <div className="text-center py-12 bg-card rounded-lg border border-dashed">
                <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold">Geen resultaten gevonden</h3>
                <p className="text-muted-foreground">Probeer een andere zoekterm of schakel de 24/7 filter uit.</p>
            </div>
        )}
      </main>

      <Button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        variant="default"
        className={cn(
          'fixed bottom-6 right-3 z-[100] h-14 w-10 rounded-xl bg-primary text-primary-foreground shadow-lg hover:bg-primary/90',
          'transition-all duration-300 ease-in-out',
          showScrollTopButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        )}
        size="icon"
        tabIndex={showScrollTopButton ? 0 : -1}
        aria-hidden={!showScrollTopButton}
      >
        <ArrowUp className="h-6 w-6" />
        <span className="sr-only">Scroll naar boven</span>
      </Button>
    </div>
  );
}
